import React from 'react'
import { useFormContext } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import { HouseAvailabilityGlobalStateEnum } from '../../../../../../../lib/graphql'
import { roundedPrice } from '../../../../../../../utils'
import { Icon, TTCLabel } from '../../../../../../atoms'
import { Divider } from '../../../../../../atoms/divider/Divider'
import {
  ControlledSwitchField,
  ControlledTextField,
} from '../../../../../../forms'
import { formatMoney } from '../../../../../house-options/shared/utils/format/format-money/format-money'
import { useBigCalencarFormContext } from '../../../shared/calendar/utils/use-big-calendar-form'

type BigCalendarModalBodyGlobalProps = {
  isFormDisabled: boolean
}
export const BigCalendarModalBodyGlobal = ({
  isFormDisabled,
}: BigCalendarModalBodyGlobalProps) => {
  const intl = useIntl()
  const { modalFormProps } = useBigCalencarFormContext()

  /**
   * This is voluntarily not type, as RHF cannot handle the deps of type
   * - Would make Intellisense infinite loop
   * - Should be `useFormContext<BigCalendarModalFormProps>()`
   */
  const { control, watch, setValue } = useFormContext()

  if (!modalFormProps || modalFormProps?.kind !== 'global') {
    return (
      <div className='flex h-full w-full items-center justify-center gap-4'>
        <Icon name='cross-without-circle' size={20} color='var(--red-600)' />
        <FormattedMessage
          id='ui.BigCalendarModalBodyGlobal.form.error'
          defaultMessage="Une erreur est survenue lors de l'affichage du formulaire..."
        />
      </div>
    )
  }

  const { corporateEvening, nightlyTrip, studyDay, house } =
    modalFormProps.formValues

  const isNightlyTripBooked =
    nightlyTrip.state !== HouseAvailabilityGlobalStateEnum.FREE
  const isNightlyTripSwitchEnabled = watch('formValues.nightlyTrip.isEnabled')

  const isStudyDayBooked =
    studyDay.state !== HouseAvailabilityGlobalStateEnum.FREE
  const isStudyDaySwitchEnabled = watch('formValues.studyDay.isEnabled')

  const isCorporateEveningBooked =
    corporateEvening.state !== HouseAvailabilityGlobalStateEnum.FREE

  return (
    <div className='h-full w-full p-4'>
      {house.options.nightlyTrip.isEnabled ? (
        <div className='flex flex-col'>
          <div className='relative flex items-center justify-center'>
            <ControlledSwitchField
              label={intl.formatMessage({
                defaultMessage:
                  'Le logement est-il disponible à la nuitée à cette date ?',
                id: 'ui.BigCalendarModalBodyGlobal.form.nightlyTripAvailable',
              })}
              name='formValues.nightlyTrip.isEnabled'
              control={control}
              disabled={isNightlyTripBooked || isFormDisabled}
              inline
            />

            {isNightlyTripBooked ? (
              <span className='absolute right-0 top-2 flex h-4 w-4 items-center justify-center rounded-full bg-grey-600 p-0.5'>
                <Icon name='lock' size={10} color='var(--white)' />
              </span>
            ) : null}
          </div>

          {isNightlyTripSwitchEnabled ? (
            <ControlledTextField
              label={intl.formatMessage({
                defaultMessage: 'Prix par nuit',
                id: 'ui.BigCalendarModalBodyGlobal.form.nightlyTripPrice',
              })}
              type='number'
              control={control}
              inline
              name='formValues.nightlyTrip.options.price'
              formatValue={(value) =>
                formatMoney({ value, currency: house.currencyArea.currency })
              }
              onReset={() =>
                setValue('formValues.nightlyTrip.options.price', null, {
                  shouldDirty: true,
                })
              }
              placeholder={
                nightlyTrip.options?.price
                  ? roundedPrice(nightlyTrip.options?.price, {
                      currency: house.currencyArea.currency,
                    })
                  : intl.formatMessage({
                      defaultMessage: 'Écrire ici',
                      id: 'ui.BigCalendarModalBodyGlobal.writeHere',
                    })
              }
              disabled={isNightlyTripBooked || isFormDisabled}
            />
          ) : null}

          <Divider />
        </div>
      ) : null}

      {house.options.studyDay.isEnabled ? (
        <div className='flex flex-col'>
          <div className='relative flex items-center justify-center'>
            <ControlledSwitchField
              label={intl.formatMessage({
                defaultMessage:
                  'Le logement est-il disponible à la journée à cette date ?',
                id: 'ui.BigCalendarModalBodyGlobal.form.studyDayAvailable',
              })}
              name='formValues.studyDay.isEnabled'
              control={control}
              disabled={isStudyDayBooked || isFormDisabled}
              inline
            />

            {isStudyDayBooked ? (
              <span className='absolute right-0 top-2 flex h-4 w-4 items-center justify-center rounded-full bg-grey-600 p-0.5'>
                <Icon name='lock' size={10} color='var(--white)' />
              </span>
            ) : null}
          </div>

          {isStudyDaySwitchEnabled ? (
            <ControlledTextField
              label={
                <>
                  <FormattedMessage
                    defaultMessage='Prix par personne du séjour à la journée'
                    id='ui.BigCalendarModalBodyGlobal.form.studyDayPrice'
                  />
                  <TTCLabel />
                </>
              }
              control={control}
              type='number'
              inline
              name='formValues.studyDay.options.price'
              formatValue={(value) =>
                formatMoney({ value, currency: house.currencyArea.currency })
              }
              onReset={() =>
                setValue('studyDay.options.price', null, { shouldDirty: true })
              }
              placeholder={
                studyDay.options?.price
                  ? roundedPrice(studyDay.options?.price, {
                      currency: house.currencyArea.currency,
                    })
                  : intl.formatMessage({
                      defaultMessage: 'Écrire ici',
                      id: 'ui.BigCalendarModalBodyGlobal.writeHere',
                    })
              }
              disabled={isStudyDayBooked || isFormDisabled}
            />
          ) : null}

          <Divider />
        </div>
      ) : null}

      {house.options.corporateEvening.isEnabled ? (
        <div className='flex flex-col'>
          <div className='relative flex items-center justify-center'>
            <ControlledSwitchField
              label={intl.formatMessage({
                defaultMessage:
                  "Le logement est-disponible pour une soirée d'entreprise à cette date ?",
                id: 'ui.BigCalendarModalBodyGlobal.form.corporateEveningAvailable',
              })}
              name='formValues.corporateEvening.isEnabled'
              control={control}
              disabled={isCorporateEveningBooked || isFormDisabled}
              inline
            />

            {isCorporateEveningBooked ? (
              <span className='absolute right-0 top-2 flex h-4 w-4 items-center justify-center rounded-full bg-grey-600 p-0.5'>
                <Icon name='lock' size={10} color='var(--white)' />
              </span>
            ) : null}
          </div>
          <Divider />
        </div>
      ) : null}
    </div>
  )
}
